import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { isCourtBasedSportEnv } from '../../shared/sport-terminology';
import { environment } from '../../../environments/environment';
import {
  getEnvDefaultUiLanguage,
  getManualUiLanguage,
  persistManualUiLanguage,
} from '../utils/client-language.util';

export interface Language {
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'selectedLanguage';
  private readonly FALLBACK_LANG = 'en';

  private availableLanguages: Language[] = [];

  private readonly currentLanguageSubject = new BehaviorSubject<string>(this.FALLBACK_LANG);
  currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private readonly translateService: TranslateService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    this.initializeAvailableLanguages();

    this.translateService.addLangs(this.availableLanguages.map(lang => lang.code));
    this.translateService.setFallbackLang(this.FALLBACK_LANG);

    const savedLang = this.getSavedLanguage();
    if (savedLang) {
      this.setLanguage(savedLang, true);
      return;
    }
    this.setLanguage(this.getDefaultLang(), false);
  }

  private getDefaultLang(): string {
    return getEnvDefaultUiLanguage();
  }

  private isSbtfTenant(): boolean {
    const tenant = environment.tenant_Name;
    return tenant === 'sbtf' || tenant === 'sbtf_test';
  }

  private initializeAvailableLanguages(): void {
    const allLanguages: Language[] = [
      { code: 'en', name: 'English' },
      { code: 'norway', name: 'Norwegian' },
      { code: 'swedan', name: 'Swedish' },
      { code: 'sv', name: 'Slovenian' },
      { code: 'spain', name: 'Spanish' },
      { code: 'cs', name: 'Czech' },
      { code: 'da', name: 'Danish' },
      { code: 'de', name: 'German' },
      { code: 'fr', name: 'French' },
      { code: 'it', name: 'Italian' }
    ];

    const limitedLanguages: Language[] = [
      { code: 'en', name: 'English' },
      { code: 'de', name: 'German' },
      { code: 'fr', name: 'French' },
      { code: 'it', name: 'Italian' }
    ];

    this.availableLanguages = isCourtBasedSportEnv()
      ? limitedLanguages
      : allLanguages;
  }

  private getSavedLanguage(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const savedLang = getManualUiLanguage();
    if (this.isSbtfTenant() && savedLang === 'sv') {
      return null;
    }
    if (savedLang && this.isLanguageAvailable(savedLang)) {
      return savedLang;
    }
    return null;
  }

  private isLanguageAvailable(langCode: string): boolean {
    return this.availableLanguages.some(lang => lang.code === langCode);
  }

  setLanguage(langCode: string, persistManual = true): boolean {
    if (!this.isLanguageAvailable(langCode)) {
      console.warn(`Language ${langCode} is not available. Using fallback language.`);
      langCode = this.getDefaultLang();
    }

    this.translateService.use(langCode);
    this.currentLanguageSubject.next(langCode);

    if (isPlatformBrowser(this.platformId) && persistManual) {
      persistManualUiLanguage(langCode);
      localStorage.setItem(this.STORAGE_KEY, langCode);
    }

    return true;
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  getAvailableLanguages(): Language[] {
    return [...this.availableLanguages];
  }

  getTextWithParams(key: string, params?: object): Observable<string> {
    return this.translateService.get(key, params);
  }
}
